import { buildHeadersWithWidgetToken } from '@elfsight/lib-utils';
import { chunk } from 'es-toolkit';
import { SourceTypes } from '../../../../constants';
import { formatText, handleResponseError } from '../../utils';
import type { DataValue, PictureValueType, ProviderType } from '../types';
import type {
  YoutubeChannelValue,
  YoutubeResponseItemType,
  YoutubeResponseType,
  YoutubeSourceValue
} from './types';
import { buildVideosApiURL, getVideoIDFromPlaylist } from './utils';

const YOUTUBE_SITE = 'https://www.youtube.com';
const PLATFORM = SourceTypes.YOUTUBE;

function getChannelData({
  id,
  snippet: {
    title: name,
    thumbnails: {
      medium: { url: thumbnail }
    }
  }
}: YoutubeChannelValue) {
  const url = `${YOUTUBE_SITE}/channel/${id}`;

  return { name, thumbnail, url };
}

function getVideoData({
  id,
  snippet: { publishedAt, title, description, thumbnails },
  statistics: { commentCount, likeCount, viewCount, favoriteCount }
}: YoutubeResponseItemType) {
  const url = `${YOUTUBE_SITE}/watch?v=${id}`;

  return {
    id,
    url,
    publishedAt,
    title,
    description,
    commentCount,
    likeCount,
    viewCount,
    favoriteCount,
    thumbnail: thumbnails
  };
}

const select = (
  response: YoutubeResponseType
): DataValue<PictureValueType[]>[] => {
  handleResponseError(response, PLATFORM, 'items');

  const { items: videos, channel } = response;

  const channelData = getChannelData(channel);

  return videos.map((video) => {
    const videoData = getVideoData(video);

    return {
      id: videoData.id,
      source: {
        type: PLATFORM,
        url: videoData.url
      },
      text: formatText(videoData.description, PLATFORM),
      timestamp: Date.parse(videoData.publishedAt),
      media: {
        type: 'video',
        src: videoData.url,
        thumbnail: Object.values(videoData.thumbnail)
      },
      user: {
        name: channelData.name,
        image: channelData.thumbnail,
        url: channelData.url
      },
      counters: {
        likes: parseInt(videoData.likeCount, 10),
        comments: parseInt(videoData.commentCount, 10)
      }
    };
  });
};

export type YoutubePreparedSourceValue = YoutubeSourceValue & {
  youtubePreparedSource: string[];
};

export async function prepareYoutubeSource(
  source: YoutubeSourceValue | undefined,
  widgetToken: string
): Promise<YoutubePreparedSourceValue | undefined> {
  if (!source?.youtubeSource) {
    return;
  }

  const { youtubeSource } = source;

  const videoIDs = await getVideoIDFromPlaylist(youtubeSource, widgetToken);
  const chunked = chunk(videoIDs, 50);
  const youtubePreparedSource = chunked.map((singleChunk) =>
    buildVideosApiURL(singleChunk)
  );

  return { ...source, youtubePreparedSource };
}

const createPageFunction = (sources: string[]) => {
  let index = 0;

  return () => {
    if (index < sources.length) {
      const currentValue = sources[index];
      index += 1;

      return currentValue;
    }

    return undefined;
  };
};

export function createYoutubeSourceProvider(
  {
    youtubePreparedSource,
    youtubeChannel,
    youtubeSource = ''
  }: YoutubePreparedSourceValue,
  globalKey: string[],
  filtersKey: string[],
  widgetToken: string
): ProviderType<YoutubeResponseType> | undefined {
  if (!youtubePreparedSource) {
    return;
  }

  const key = [...globalKey, ...filtersKey, youtubeSource];

  const preparedSelect = (response: YoutubeResponseType) => {
    const modifiedResponse = { ...response, channel: youtubeChannel! };

    return select(modifiedResponse);
  };

  const page = createPageFunction(youtubePreparedSource);

  // Custom Fetch необходим для пробрасывания widget token в запрос.
  // От него можно отказаться после добавления поддержки widget token в useAggregateFetcher
  const customFetch = async (nextPageURL: string) => {
    const headers = buildHeadersWithWidgetToken(widgetToken);

    return fetch(nextPageURL, { headers });
  };

  return { key, page, select: preparedSelect, fetch: customFetch };
}
